import { arcRender, type ArcRender, type DotRender } from './decor'
import {
  blendExpression,
  exprDistance,
  exprMorphBlendT,
  exprMorphDuration,
  type BotExpression
} from './expressions'
import { decalageDesYeux } from './eyefit'
import { blinkScale, eyePoses, liveliness, REST_GAZE, type HeadGaze } from './face'
import { isSparkleSilhouette, petaBodyAmount, roundBodyAmount, starBodyAmount, type UniverseDecor } from './losi'
import { expressionAccent, forcedBlinkLid } from './motion'
import { clamp, easings, lerp, r2 } from './math'
import {
  pocketEyeSlide,
  pocketRailDelta,
  pocketRollLift,
  pocketSpreadScale,
  starNearHigherLift
} from './pocketFollow'
import { type ShapeEyeStyle, UNIT_EYE, agentConcaveGaze, animationPocketRoll, concavePocketStyle, expressionPocketLift, eyePairNestOffset, eyePairStarHeadOffset, eyePairStarOrbitSeats, eyeStyleForState, eyeSurfaceFit, isConcavePocketState, lerpGazeBias, pocketGazeBias, volumeBodyLiftScale, shapeGazeBias, softConcaveGaze, starEyeMix, starFollowGapMood, petaSeatGaze, PETA_REST_GAZE } from './shapeEyes'
import {
  blend,
  closedPath,
  eyePath,
  snapToOutline,
  starVolumeRadii,
  starVolumeSheen,
  roundVolumeRadii,
  petaVolumeRadii,
  bodyGaze,
  ROUND_BODY_DAMP,
  PETA_BODY_DAMP,
  toPoints,
  type Point,
  type Silhouette,
  type VolumeSheen
} from './shape'
import { isFacelessShape } from './skins'
import { STATE_BY_ID, type Pose, type StateDef, type StateId } from './states'

export interface RenderedEye {
  d: string
  matrix: string
  alpha: number
}

export interface BotFrame {
  bodyPath: string
  /** Unused — the body is one path. Kept so frames stay a fixed shape. */
  bodyPathBack: string | null
  bodyPathFront: string | null
  bodyAlpha: number
  eyes: RenderedEye[]
  dots: DotRender[]
  /** true = dots render behind the body (burst particles) */
  dotsBehind: boolean
  arcs: ArcRender[]
  notif: { x: number; y: number; r: number } | null
  notch: { x: number; y: number; r: number } | null
  /** Orbit ring + satellite: inspired by the logotype, positioned by the component. */
  universe?: UniverseDecor | null
  /** True when the body is the sparkle star — LosiBot adds orbit decor. */
  showUniverse: boolean
  /** Composed gaze at this instant — the satellite seats on this sphere. */
  gaze: HeadGaze
  /** Body translation in viewBox units (breath / drift). */
  bodyOffset: { x: number; y: number }
  /** Look-side paper sheen. Null when the body has no volume, or face-on. */
  volumeSheen: VolumeSheen | null
  /**
   * Gaze that drives body foreshortening / lean (REST when face-on). The Saturn
   * ring seats on this so it turns with the body, not the full eye mix.
   */
  bodyGaze: HeadGaze
}

/**
 * Where the bot directs its gaze when something external drives it — the mouse
 * pointer, today.
 *
 * `yaw` and `pitch` are ABSOLUTE directions, which replace those of the pose
 * as `mix` rises. Two reasons, each a trap already fallen into:
 *
 * - the ENGINE must do this blending, not the caller, because only it knows the
 *   pose AT THIS INSTANT. A caller that compensated for the expression's
 *   orientation would read its arrival value while the morph was still in
 *   progress, and the eyes would jump on every mood change;
 * - and it must be absolute on BOTH axes. In relative terms, eye height followed
 *   each expression's — "neutral" looks at +28.6° when the others are between
 *   -9 and +9 — so the eyes dropped abruptly on the first mood change. What
 *   gives an expression its character during tracking is the SHAPE of its eyes
 *   (squinted, round, asymmetric), not where it looks: the cursor decides that.
 *
 * `mix` says how much the external input commands DIRECTION (0 = not at all).
 *
 * `wander` says, separately, how much automatic drift remains. The two must not
 * be conflated: when the pointer moves, drift must fade out — combined, the bot
 * would look like it was searching for the cursor without ever holding it. But
 * when there is NO pointer (keyboard arrival, touch, or mouse leaving the
 * window), the head must stay turned AND keep living. Conflating them froze
 * the gaze as soon as the view opened.
 *
 * `spin` is a full turn to travel ALONG THE WAY, in degrees, melted toward 0 on
 * arrival. Because the eyes live on a sphere, a turn sends them behind the ball
 * and back from the other side — and since `-360°` is the same angle as `0`, it
 * does not change where they settle.
 */
export interface Look {
  yaw: number
  pitch: number
  mix: number
  spin: number
  wander: number
  /**
   * Normalised cursor on the pocket rail (−1…1). Per-eye arc in screen space on
   * concave shapes; linear slide on the circle.
   */
  pocket?: { nx: number; ny: number }
  /**
   * Body volume / lean amount (0…1). Defaults to `mix`. Settings keeps `mix: 1`
   * at panel home so eyes stay turned — without a separate volume, the sparkle
   * stayed full-foreshortened (snout) while free-look at centre stays face-on.
   */
  volume?: number
  /** @deprecated Common tangent slide — use `pocket` for free follow. */
  tangent?: { x: number; y: number }
}

const NO_LOOK: Look = { yaw: 0, pitch: 0, mix: 0, spin: 0, wander: 1 }

const lerpTangent = (
  a: Look['tangent'],
  b: Look['tangent'],
  t: number
): Look['tangent'] => {
  if (!a && !b) return undefined
  return { x: lerp(a?.x ?? 0, b?.x ?? 0, t), y: lerp(a?.y ?? 0, b?.y ?? 0, t) }
}

const lerpPocket = (
  a: Look['pocket'],
  b: Look['pocket'],
  t: number
): Look['pocket'] => {
  if (!a && !b) return undefined
  return { nx: lerp(a?.nx ?? 0, b?.nx ?? 0, t), ny: lerp(a?.ny ?? 0, b?.ny ?? 0, t) }
}

const lerpLook = (a: Look, b: Look, t: number): Look => ({
  yaw: lerp(a.yaw, b.yaw, t),
  pitch: lerp(a.pitch, b.pitch, t),
  mix: lerp(a.mix, b.mix, t),
  spin: lerp(a.spin, b.spin, t),
  wander: lerp(a.wander, b.wander, t),
  pocket: lerpPocket(a.pocket, b.pocket, t),
  volume:
    a.volume !== undefined || b.volume !== undefined
      ? lerp(a.volume ?? a.mix, b.volume ?? b.mix, t)
      : undefined,
  tangent: lerpTangent(a.tangent, b.tangent, t)
})

const lerpEye = (a: Pose['eyes'][number], b: Pose['eyes'][number], t: number) => ({
  w: lerp(a.w, b.w, t),
  h: lerp(a.h, b.h, t),
  open: lerp(a.open, b.open, t),
  tilt: lerp(a.tilt ?? 0, b.tilt ?? 0, t),
  form: t >= 0.5 ? (b.form ?? 'capsule') : (a.form ?? 'capsule')
})

function lerpEyeStyle(a: ShapeEyeStyle, b: ShapeEyeStyle, t: number): ShapeEyeStyle {
  return {
    w: lerp(a.w, b.w, t),
    h: lerp(a.h, b.h, t),
    spread: lerp(a.spread, b.spread, t),
    lift: lerp(a.lift, b.lift, t),
    inset: lerp(a.inset, b.inset, t),
    tilt: lerp(a.tilt, b.tilt, t)
  }
}

/** Interpolation of two poses. Decor cross-fades in opacity, not geometry. */
function blendPose(a: Pose, b: Pose, t: number): Pose {
  const out = 1 - t
  return {
    sil: blend(a.sil, b.sil, t),
    offX: lerp(a.offX, b.offX, t),
    offY: lerp(a.offY, b.offY, t),
    gaze: {
      yaw: lerp(a.gaze.yaw, b.gaze.yaw, t),
      pitch: lerp(a.gaze.pitch, b.gaze.pitch, t),
      roll: lerp(a.gaze.roll, b.gaze.roll, t)
    },
    split: lerp(a.split, b.split, t),
    eyes: [lerpEye(a.eyes[0], b.eyes[0], t), lerpEye(a.eyes[1], b.eyes[1], t)],
    eyeAlpha: lerp(a.eyeAlpha, b.eyeAlpha, t),
    bodyAlpha: lerp(a.bodyAlpha, b.bodyAlpha, t),
    dots: [
      ...a.dots.map((d) => ({ ...d, opacity: d.opacity * out })),
      ...b.dots.map((d) => ({ ...d, opacity: d.opacity * t }))
    ],
    arcs: [
      ...a.arcs.map((r) => ({ ...r, id: `a${r.id}`, opacity: r.opacity * out })),
      ...b.arcs.map((r) => ({ ...r, id: `b${r.id}`, opacity: r.opacity * t }))
    ],
    // the notification dot belongs to one of the two states only; it does not blend
    notif: t < 0.5 ? a.notif : b.notif,
    dotsBehind: t < 0.5 ? a.dotsBehind : b.dotsBehind
  }
}

/**
 * Clockless engine: `sample(t)` is a pure function of time.
 *
 * Practical consequence: pause, resume, slow motion, and jumping to an arbitrary
 * timestamp all yield exactly the same image, and rendering is testable without a DOM.
 */
export class BotEngine {
  /** ball radius at rest, in viewBox units */
  readonly scale: number

  private cur: StateId
  private prev: StateId | null = null
  /**
   * FROZEN starting pose, set only when a state change arrives while a fade is
   * already in progress. See `setState`.
   */
  private departFige: Pose | null = null
  private tCur = 0
  private tPrev = 0
  private blinkAt = -10
  private pts: Point[] = []
  private shape: number[] | null = null
  private shapePrev: number[] | null = null
  private shapeAt = -10
  private expr: BotExpression | null = null
  private exprPrev: BotExpression | null = null
  private exprAt = -10
  private exprMorphDur = 0.45
  private exprMorphDist = 0
  private look: Look = NO_LOOK
  private lookPrev: Look = NO_LOOK
  private lookAt = -10
  /** current catch-up duration; see `LOOK_MORPH`, its default value */
  private lookMorph = 0.24
  /** Prop-driven body icon (`glyph` state); morphs like `setShape`. */
  private icon: { radii: number[]; cy: number; rot?: number; dots: DotRender[] } | null = null
  private iconPrev: { radii: number[]; cy: number; rot?: number; dots: DotRender[] } | null = null
  private iconAt = -10

  /** morph duration when changing body shape */
  static readonly SHAPE_MORPH = 0.45

  /**
   * Catch-up duration for gaze toward the target. Shorter than `SHAPE_MORPH`:
   * a gaze that tracks must feel attentive, not viscous. Because the target is
   * reset on every mouse move, this duration gives tracking its inertia — the
   * gaze never quite reaches a cursor that keeps moving.
   */
  static readonly LOOK_MORPH = 0.24

  constructor(
    scale = 100,
    initial: StateId = 'idle',
    shape: number[] | null = null,
    expression: BotExpression | null = null
  ) {
    this.scale = scale
    this.cur = initial
    this.shape = shape
    this.expr = expression
  }

  /**
   * Rest expression chosen in the customiser. Like shape, it eases toward the
   * new value instead of jumping.
   */
  setExpression(expression: BotExpression | null, now = 0) {
    if (expression === this.expr) return
    this.exprPrev = this.expr
    this.expr = expression
    this.exprAt = now
    if (this.exprPrev && expression) {
      this.exprMorphDist = exprDistance(this.exprPrev, expression)
      this.exprMorphDur = exprMorphDuration(this.exprPrev, expression)
    } else {
      this.exprMorphDist = 0
      this.exprMorphDur = BotEngine.SHAPE_MORPH
    }
    if (STATE_BY_ID.get(this.cur)?.baseFace) this.blinkAt = now
  }

  /** Fade expression body accents in after a morph settles — never during it. */
  private accentRamp(now: number): number {
    if (this.exprAt < 0) return 1
    const end = this.exprAt + this.exprMorphDur
    if (now < end) return 0
    const k = (now - end) / 0.15
    return k >= 1 ? 1 : easings.easeOutCubic(clamp(k))
  }

  private inShapeMorph(now: number): boolean {
    if (!this.shapePrev || !this.shape) return false
    return now - this.shapeAt < BotEngine.SHAPE_MORPH + 0.2
  }

  private inExprMorph(now: number): boolean {
    if (this.exprAt < 0 || !this.exprPrev || !this.expr) return false
    return now - this.exprAt < this.exprMorphDur + 0.2
  }

  /** Expression morph blend factor — 1 when settled. */
  private exprMorphK(now: number): number {
    const to = this.expr
    const from = this.exprPrev
    if (!to || !from || this.exprAt < 0) return 1
    const k = (now - this.exprAt) / this.exprMorphDur
    if (k >= 1) return 1
    return exprMorphBlendT(k, this.exprMorphDur, this.exprMorphDist)
  }

  private exprAtTime(now: number): BotExpression | null {
    const to = this.expr
    const from = this.exprPrev
    if (!to || !from) return to
    const k = (now - this.exprAt) / this.exprMorphDur
    if (k >= 1) return to
    const t = exprMorphBlendT(k, this.exprMorphDur, this.exprMorphDist)
    return blendExpression(from, to, t)
  }

  /**
   * Shape chosen in the customiser. It replaces the body only on rest states
   * (`baseBody`): on others, the silhouette IS the animation and must not be
   * overwritten.
   *
   * The change is a morph, not a snap: because all shapes are sampled at the
   * same angles, interpolating the radii is enough.
   */
  setShape(radii: number[] | null, now = 0) {
    if (radii === this.shape) return
    this.shapePrev = this.shape
    this.shape = radii
    this.shapeAt = now
    if (STATE_BY_ID.get(this.cur)?.baseFace) this.blinkAt = now
  }

  /**
   * Body icon for the `glyph` state (`bang` / `amp` / custom radii). Morphs like
   * `setShape`. No-op when the payload is unchanged.
   */
  setIcon(
    next: { radii: number[]; cy: number; rot?: number; dots: DotRender[] } | null,
    now = 0
  ) {
    const same =
      (!next && !this.icon) ||
      (!!next &&
        !!this.icon &&
        next.cy === this.icon.cy &&
        (next.rot ?? 0) === (this.icon.rot ?? 0) &&
        next.radii.length === this.icon.radii.length &&
        next.radii.every((r, i) => r === this.icon!.radii[i]))
    if (same) return
    this.iconPrev = this.icon
    this.icon = next
    this.iconAt = now
  }

  private iconAtTime(
    now: number
  ): { radii: number[]; cy: number; rot?: number; dots: DotRender[] } | null {
    const to = this.icon
    if (!to) return null
    const from = this.iconPrev
    if (!from) return to
    const k = (now - this.iconAt) / BotEngine.SHAPE_MORPH
    if (k >= 1) return to
    const t = easings.easeOutQuint(clamp(k))
    let dRot = (to.rot ?? 0) - (from.rot ?? 0)
    while (dRot > Math.PI) dRot -= Math.PI * 2
    while (dRot < -Math.PI) dRot += Math.PI * 2
    return {
      radii: to.radii.map((r, i) => lerp(from.radii[i] ?? r, r, t)),
      cy: lerp(from.cy, to.cy, t),
      rot: (from.rot ?? 0) + dRot * t,
      dots: [
        ...from.dots.map((d) => ({ ...d, opacity: d.opacity * (1 - t) })),
        ...to.dots.map((d) => ({ ...d, opacity: d.opacity * t }))
      ].filter((d) => d.opacity > 0.02)
    }
  }

  /**
   * Effective shape at instant `now`, including any morph in progress.
   *
   * Does NOT reset `shapePrev` to null at the end of the morph: `sample` must
   * remain a pure function of time, so re-reading a past timestamp must return
   * the intermediate image. We just keep one extra reference.
   */
  private shapeAtTime(now: number): number[] | null {
    const to = this.shape
    const from = this.shapePrev
    if (!to || !from) return to
    const k = (now - this.shapeAt) / BotEngine.SHAPE_MORPH
    if (k >= 1) return to
    const t = easings.easeOutQuint(clamp(k))
    // allocated only during the morph; outside morph we return the array as-is
    return to.map((r, i) => lerp(from[i] ?? r, r, t))
  }

  /**
   * New gaze target, `null` to revert to the state's own gaze.
   *
   * It restarts from the CURRENT value, not the previous target like `setShape`:
   * this method is called on every pointer move, and restarting from the old
   * target would pull the gaze back a step before each catch-up — tracking would
   * jitter instead of gliding.
   *
   * Same contract as `setShape` otherwise: external state enters through a
   * timestamped setter, never through a variable read during `sample`, or the
   * engine ceases to be a pure function of time.
   */
  setLook(look: Look | null, now: number, morph = BotEngine.LOOK_MORPH) {
    /*
     * A non-finite target is rejected. The engine KEEPS the last one: a `NaN`
     * set once would propagate on every frame and the bot would never settle
     * again. It happened for real — a `getBoundingClientRect` on a zero-size box
     * yields `0 / 0` at the caller. That one is fixed, but the engine must not
     * depend on caller caution to stay replayable.
     */
    if (look && !Number.isFinite(
      look.yaw + look.pitch + look.mix + look.spin + look.wander +
        (look.tangent?.x ?? 0) + (look.tangent?.y ?? 0) +
        (look.pocket?.nx ?? 0) + (look.pocket?.ny ?? 0)
    )) {
      return
    }
    this.lookPrev = this.lookAtTime(now)
    this.look = look ?? NO_LOOK
    this.lookAt = now
    this.lookMorph = morph
  }

  /** Effective gaze at instant `now`, including any catch-up in progress. */
  private lookAtTime(now: number): Look {
    const k = (now - this.lookAt) / this.lookMorph
    if (k >= 1) return this.look
    return lerpLook(this.lookPrev, this.look, easings.easeOutQuint(clamp(k)))
  }

  private posed(
    def: StateDef,
    t: number,
    shape: number[] | null,
    expr: BotExpression | null,
    now = 0
  ): Pose {
    let pose = def.pose(t)
    if (def.baseBody && shape) {
      // keep the pose (rotation, offset, squash) and only swap the profile
      pose = { ...pose, sil: { ...pose.sil, radii: shape } }
    }
    if (def.baseFace && expr) {
      pose = { ...pose, gaze: expr.gaze, split: expr.split, eyes: expr.eyes }
    }
    if (def.baseBody) {
      const toBlank = isFacelessShape(this.shape)
      const fromBlank = isFacelessShape(this.shapePrev)
      if (toBlank || fromBlank) {
        const k = this.shapeMorphT(now)
        const face = toBlank && fromBlank ? 0 : toBlank ? 1 - k : k
        pose = { ...pose, eyeAlpha: pose.eyeAlpha * face }
      }
    }
    // Prop-driven icon replaces the default on glyph, or freezes the icons cycle.
    if (def.id === 'glyph' || def.id === 'icons') {
      const ic = this.iconAtTime(now)
      if (ic) {
        pose = {
          ...pose,
          sil: {
            radii: ic.radii,
            rot: ic.rot ?? 0,
            cx: 0,
            cy: ic.cy,
            sx: 1,
            sy: 1
          },
          dots: ic.dots,
          eyeAlpha: 0
        }
      }
    }
    return pose
  }

  /**
   * Eye offset at instant `now` for a given state, in ball-radius units.
   *
   * It is READ from a table and interpolated, never recalculated: `eyefit.ts`
   * explains why that distinction is the entire fix. Here we only interpolate it
   * along the shape axis, with exactly the same curve and duration as the
   * silhouette morph — same cause, so it must be the same motion.
   *
   * We query the table at the morph BOUNDARIES (`shapePrev` and `shape`), not at
   * the profile returned by `shapeAtTime`: that one is a fresh array allocated
   * every frame, so it has no identity and exists in no table.
   */
  private decalageAtTime(now: number, state: StateId): { x: number; y: number } {
    /**
     * One morph axis: read the table at its two BOUNDARIES and interpolate with
     * its curve. Never on the interpolated value — that one has no identity and
     * exists in no table, and feeding it to the table is what made earlier
     * versions tremble.
     */
    const surAxe = (
      debut: number,
      duree: number,
      a: { x: number; y: number },
      b: { x: number; y: number },
      progress: (k: number) => number = easings.easeOutQuint
    ) => {
      if (a === b) return b
      const k = (now - debut) / duree
      if (k >= 1) return b
      const t = progress(clamp(k))
      return { x: lerp(a.x, b.x, t), y: lerp(a.y, b.y, t) }
    }

    const parForme = (radii: number[] | null) =>
      surAxe(
        this.exprAt,
        this.exprMorphDur,
        decalageDesYeux(radii, state, this.exprPrev?.id ?? null),
        decalageDesYeux(radii, state, this.expr?.id ?? null)
      )

    // then the shape axis
    return surAxe(
      this.shapeAt,
      BotEngine.SHAPE_MORPH,
      parForme(this.shapePrev),
      parForme(this.shape)
    )
  }

  /** Progress along the customiser-shape morph axis (0 → 1). */
  private shapeMorphT(now: number): number {
    const k = (now - this.shapeAt) / BotEngine.SHAPE_MORPH
    if (k >= 1 || this.shapePrev === this.shape) return 1
    return easings.easeOutQuint(clamp(k))
  }

  get state(): StateId {
    return this.cur
  }

  get prevState(): StateId | null {
    return this.prev
  }

  /** Eased morph progress for the current state (0 → 1), same curve as `sample`. */
  morphRatio(now: number): number {
    const def = STATE_BY_ID.get(this.cur)!
    const since = now - this.tCur
    if (since >= def.morph) return 1
    return easings.easeOutQuint(clamp(since / def.morph))
  }

  /** Seconds since the current state was timestamped — pose local time. */
  age(now: number): number {
    return Math.max(0, now - this.tCur)
  }

  /**
   * Restarts on `id` with NO previous state, like a fresh engine placed on that state.
   *
   * That is what "rewind" means for this engine. `setState` alone cannot do it:
   * it keeps the departed state to fade from, which is exactly its role during
   * playback, and exactly what we must not do when returning to the start of a
   * sequence. Replaying frame 0 after a full pass blended the first state with
   * the LAST one, and the GIF export opened on a ball with no eyes — comet has
   * zero `eyeAlpha`.
   *
   * `sample` remains a pure function of time: like `setState`, this is a
   * TIMESTAMPED setter, called by the sequence driver, never during sampling.
   */
  reset(id: StateId, now: number) {
    this.cur = id
    this.prev = null
    this.departFige = null
    this.tCur = now
    this.tPrev = now
    this.blinkAt = -10
  }

  /**
   * Origin of the fade in progress: the frozen pose if there is one, otherwise
   * the departed state evaluated at its own elapsed time — still animating, which
   * is intentional.
   */
  private origine(
    now: number,
    shape: number[] | null,
    expr: BotExpression | null
  ): Pose | null {
    if (this.departFige) return this.departFige
    if (!this.prev) return null
    const prevDef = STATE_BY_ID.get(this.prev)!
    return this.posed(prevDef, Math.max(0, now - this.tPrev), shape, expr, now)
  }

  /**
   * Composite pose at instant `now`, fade in progress included: exactly what
   * `sample` blends, before the at-rest life layer and gaze. Extracted so
   * `setState` can freeze it.
   */
  private poseComposee(now: number): Pose {
    const def = STATE_BY_ID.get(this.cur)!
    const shape = this.shapeAtTime(now)
    const expr = this.exprAtTime(now)
    const pose = this.posed(def, Math.max(0, now - this.tCur), shape, expr, now)
    const since = now - this.tCur
    if (since >= def.morph) return pose
    const origine = this.origine(now, shape, expr)
    if (!origine) return pose
    return blendPose(origine, pose, easings.easeOutQuint(clamp(since / def.morph)))
  }

  /**
   * State change, timestamped.
   *
   * The engine keeps only ONE history slot, so a change during a fade replaced
   * the blend origin with the FULL pose of the state being left, instead of the
   * partially blended image on screen. Measured on `idle -> wide -> idle` at
   * 100 ms: 35.9 px jump vs 8.0 px normal motion.
   *
   * So we freeze the current composite pose and blend from it. Continuous by
   * construction, no matter how many changes are chained.
   *
   * And ONLY in that case. Freezing on every change would halt the departed
   * state's animation for the entire fade — `alert`'s "!" would freeze mid-run —
   * when there is nothing to fix outside a morph: the departed state is already
   * exactly the displayed image. Montage playback, whose blocks last at least
   * the longest fade (`MIN_BLOCK`), therefore never freezes anything and renders
   * bit-identically to before.
   */
  setState(id: StateId, now: number) {
    if (id === this.cur) return
    const morph = STATE_BY_ID.get(this.cur)!.morph
    const enPleinFondu = this.prev !== null && now - this.tCur < morph
    this.departFige = enPleinFondu ? this.poseComposee(now) : null
    this.prev = this.cur
    this.tPrev = this.tCur
    this.cur = id
    this.tCur = now
    // In the video, each shape change is masked by a blink.
    if (STATE_BY_ID.get(id)?.blinkIn) this.blinkAt = now
  }

  sample(now: number): BotFrame {
    const R = this.scale
    const def = STATE_BY_ID.get(this.cur)!
    const shape = this.shapeAtTime(now)
    const expr = this.exprAtTime(now)
    let pose = this.posed(def, Math.max(0, now - this.tCur), shape, expr, now)
    let decalage = this.decalageAtTime(now, this.cur)

    // --- transition -------------------------------------------------------
    const since = now - this.tCur
    // The previous state is never purged: `since < def.morph` is enough to
    // ignore it once the fade is over, and forgetting it would make the engine
    // non-replayable — re-reading a timestamp before the fade ended would not
    // find it again. The optimization that looks innocent and breaks everything.
    const origine = since < def.morph ? this.origine(now, shape, expr) : null
    const fade = origine ? easings.easeOutQuint(clamp(since / def.morph)) : 1
    if (origine) {
      // Exponential ease-out: the curve measured from the video. The body has no
      // overshoot (only the notification dot and eye opening do).
      // The ratio is clamped: re-reading a timestamp BEFORE the state change
      // would yield a negative ratio, which the ease-out extrapolates — the
      // silhouette then travels thirty times too far.
      pose = blendPose(origine, pose, fade)
      // Eye offset follows the SAME curve as the silhouette that motivates it. It
      // comes from the departed state, which `setState` always sets alongside the
      // origin — the test is for typing, not a real case.
      const quitte = this.prev
      if (quitte) {
        const avant = this.decalageAtTime(now, quitte)
        decalage = {
          x: lerp(avant.x, decalage.x, fade),
          y: lerp(avant.y, decalage.y, fade)
        }
      }
    }

    // Sparkle face orbit: eyefit clears the mask (often shoving +Y into the
    // waist); lift the cleared pair under the top tip — the realistic band.
    // Only on baseBody: measured silhouettes must ignore the customiser shape.
    // Big open eyes get less lift so they still clear the outline.
    if (def.baseBody && starBodyAmount(this.shape ?? []) > 0.5) {
      const eyeH = expr
        ? Math.max(expr.eyes[0]!.h, expr.eyes[1]!.h)
        : 0.412
      const liftK = clamp(1.2 - eyeH * 1.35, 0.4, 1)
      decalage = { x: decalage.x * 0.9, y: decalage.y * 0.7 - 0.14 * liftK }
    }

    // --- life at rest -----------------------------------------------------
    const alive = pose.eyeAlpha > 0.01
    const look = this.lookAtTime(now)
    const enMorph = this.inExprMorph(now) || this.inShapeMorph(now)
    const agentLoop = def.id.startsWith('agent') && !def.baseFace
    const tan = look.tangent
    const pocket = look.pocket
    const pocketRadii = def.baseBody ? this.shape : pose.sil.radii
    const tracking =
      look.mix > 0.12 ||
      (tan !== undefined && tan.x * tan.x + tan.y * tan.y > 1e-8) ||
      (pocket !== undefined && pocket.nx * pocket.nx + pocket.ny * pocket.ny > 1e-8)
    const life = liveliness(now, {
      wander: alive && !enMorph && !agentLoop ? look.wander : 0,
      blink: alive && def.baseFace && !enMorph,
      float: !enMorph && !tracking,
      saccade: !enMorph && !agentLoop && !tracking
    })

    const shapeMorph = def.baseBody ? this.shapeMorphT(now) : 1
    const concavePocket = isConcavePocketState(def) && !def.id.startsWith('agent')
    const pocketGazeOn = (radii: number[] | null, e: BotExpression | null) =>
      pocketGazeBias(
        radii,
        e?.gaze ?? pose.gaze,
        def.baseFace ? e : null,
        concavePocket,
        def.id
      )
    const pocketGazeShape = (e: BotExpression | null) =>
      lerpGazeBias(pocketGazeOn(this.shapePrev, e), pocketGazeOn(this.shape, e), shapeMorph)
    const exprK = this.exprMorphK(now)
    const seatedBias = def.baseBody
      ? def.baseFace && this.exprPrev && this.expr
        ? lerpGazeBias(pocketGazeShape(this.exprPrev), pocketGazeShape(this.expr), exprK)
        : pocketGazeShape(def.baseFace ? expr : null)
      : pocketGazeOn(pocketRadii, null)
    // Absolute look replaces pose pitch — drop expression/catalogue seating so
    // it does not stack on top of `look.pitch`. Shape bias still seats the waist.
    const shapeBias = def.baseBody
      ? lerpGazeBias(shapeGazeBias(this.shapePrev), shapeGazeBias(this.shape), shapeMorph)
      : shapeGazeBias(pocketRadii)
    // Sparkle free-look: loaders/agents use keepPose (mix=0) but still pass a
    // pocket. Drive eyes + body volume from that pocket so the pair does not
    // slide in 2D while the sparkle stays face-on.
    const starRadiiFollow = def.baseBody
      ? (this.shape ?? pose.sil.radii)
      : pose.sil.radii
    const starAmtFollow = starBodyAmount(starRadiiFollow)
    const roundAmtFollow = def.baseBody ? roundBodyAmount(starRadiiFollow) : 0
    const petaAmtFollow = def.baseBody ? petaBodyAmount(starRadiiFollow) : 0
    const bodyVolFollow = Math.max(starAmtFollow, roundAmtFollow, petaAmtFollow)
    const pocketDist =
      pocket !== undefined ? Math.hypot(pocket.nx, pocket.ny) : 0
    const followAmt =
      look.mix > 0.01
        ? look.mix
        : pocketDist > 0.02 && bodyVolFollow > 0.5
          ? clamp(pocketDist / 0.18)
          : 0
    // Body foreshortening can track pointer excursion separately from eye mix
    // (settings panel home: mix=1, volume≈0).
    const volAmt = look.volume !== undefined ? clamp(look.volume, 0, 1) : followAmt
    // Sparkle: almost-full sphere look so eyes leave the REST (up-right) pocket
    // — especially neutre. Tip-cap / left-higher stop rim rides. Body volume
    // uses `volAmt` so settings rest is not a permanent snout.
    const eyeMix =
      starAmtFollow > 0.5
        ? starEyeMix(starRadiiFollow, followAmt, look.yaw)
        : look.mix
    const gazeBias = lerpGazeBias(seatedBias, shapeBias, eyeMix)

    const accentRamp = def.baseFace ? this.accentRamp(now) : 0
    const accent =
      def.baseFace && !enMorph && expr ? expressionAccent(expr.id, now) : expressionAccent(null, 0)
    const ax = accentRamp * accent.offX
    const ay = accentRamp * accent.offY
    const aRoll = accentRamp * accent.dRoll
    const aBreath = accentRamp * (accent.breath - 1)
    const lidAccent = lerp(1, accent.lid, accentRamp)

    const seatedRoll = concavePocket
      ? animationPocketRoll(
          pose.gaze.roll,
          def.baseBody ? this.shape : pocketRadii,
          def.id
        )
      : pose.gaze.roll
    const pocketRoll =
      pocket && starBodyAmount(this.shape ?? []) > 0.5 && pocket.nx < 0
        ? (() => {
            // Flip right-high `\\` into left-high. Zero-roll moods (surpris, …)
            // still need REST's slant as the flip source — `-2 * 0` was a no-op.
            const base = Math.abs(seatedRoll) > 5 ? seatedRoll : REST_GAZE.roll
            return lerp(0, -base - seatedRoll, -pocket.nx)
          })()
        : pocket
          ? pocketRollLift(pocket.nx, pocketRadii, seatedRoll)
          : 0

    const gazeRaw = {
      // Both aimings REPLACE those of the pose instead of adding to them (see
      // `Look`), and the spin is subtracted along the way. Drift is added AFTER
      // the blend, otherwise the target would cancel it along with the pose — yet
      // it must survive a turned head with no pointer.
      yaw: lerp(pose.gaze.yaw, look.yaw, eyeMix) + life.dYaw + gazeBias.yaw - look.spin,
      pitch: lerp(pose.gaze.pitch, look.pitch, eyeMix) + life.dPitch + gazeBias.pitch,
      // roll follows nothing: the expression's head tilt is part of the face at
      // rest; catalogue faces on a concave waist ease toward idle's `\\` axis.
      roll: seatedRoll + life.dRoll + aRoll + pocketRoll
    }
    const gaze =
      def.id.startsWith('agent') && def.baseBody
        ? agentConcaveGaze(this.shape, gazeRaw)
        : def.baseBody && starBodyAmount(this.shape ?? []) > 0.5
          ? softConcaveGaze(this.shape, gazeRaw, eyeMix)
          : gazeRaw

    // blink triggered by state / expression / shape change, in addition to the schedule
    const sinceBlink = now - this.blinkAt
    const forcedLid =
      sinceBlink >= 0 && sinceBlink < 0.42 ? forcedBlinkLid(now, this.blinkAt, 0.42) : 1
    const lids: [number, number] = [
      Math.min(enMorph ? 1 : life.lids[0], forcedLid),
      Math.min(enMorph ? 1 : life.lids[1], forcedLid)
    ]

    // Sparkle / planet / peta body track pointer excursion (`volAmt`). Settings
    // keeps eye `mix` at 1 for the panel turn without locking a permanent snout.
    const agentSweep = def.id.startsWith('agent') && !def.baseFace
    const bodyDrive = look.volume !== undefined ? volAmt : followAmt
    const bodyFace: HeadGaze | null =
      bodyVolFollow > 0.5
        ? bodyDrive > 0.01
          ? { yaw: look.yaw, pitch: look.pitch, roll: 0 }
          : agentSweep
            ? { yaw: gaze.yaw, pitch: gaze.pitch, roll: 0 }
            : null
        : null
    const leanAmt = bodyFace ? (bodyDrive > 0.05 ? bodyDrive : 1) : 0
    // Round (Mickey) and Peta lean less than the sparkle — hard lean throws the
    // Saturn ring through the ears / reads as a broken silhouette. Peta even
    // softer: body lean under free-look made the whole glyph look "tilted" while
    // the eyes stayed stickers on a petal.
    const leanScale =
      petaAmtFollow > 0.5 ? 0.38 : roundAmtFollow > 0.5 ? 0.72 : 1
    const offLean =
      bodyFace && leanAmt > 0.05
        ? {
            x: leanAmt * Math.sin((bodyFace.yaw * Math.PI) / 180) * 0.2 * leanScale,
            y: leanAmt * Math.sin((-bodyFace.pitch * Math.PI) / 180) * 0.12 * leanScale
          }
        : { x: 0, y: 0 }
    const offX = pose.offX + life.driftX + ax + offLean.x
    const offY = pose.offY + life.driftY + ay + offLean.y

    // --- body -------------------------------------------------------------
    const sil: Silhouette = {
      ...pose.sil,
      cx: pose.sil.cx + offX,
      cy: pose.sil.cy + offY,
      sy: pose.sil.sy * life.breath * (1 + aBreath)
    }
    const prevBase = this.prev ? !!STATE_BY_ID.get(this.prev)?.baseBody : false
    const starAmt = starBodyAmount(sil.radii)
    const roundAmt = def.baseBody ? roundBodyAmount(sil.radii) : 0
    const petaAmt = def.baseBody ? petaBodyAmount(sil.radii) : 0
    const starK = def.baseBody
      ? starAmt * (origine && !prevBase ? fade : 1)
      : starAmt *
        (origine && prevBase
          ? 1 - fade
          : bodyDrive > 0.01 || agentSweep
            ? 1
            : 0)
    const roundK = def.baseBody
      ? roundAmt * (origine && !prevBase ? fade : 1)
      : 0
    const petaK = def.baseBody
      ? petaAmt * (origine && !prevBase ? fade : 1)
      : 0
    const volGaze: HeadGaze =
      bodyFace ??
      (roundK > 0.5
        ? // Jovi: body follows the face so ground ears turn with expressions /
          // liveliness — locking REST left the Mickey lobes glued while eyes moved.
          bodyGaze(gaze, ROUND_BODY_DAMP)
        : petaK > 0.5
          ? // Peta: same plant — five lobes must turn with the face, not stay glued.
            bodyGaze(gaze, PETA_BODY_DAMP)
          : REST_GAZE)
    // Peta's design glyph is face-on. Applying REST foreshortening at idle crushed
    // the top petal and left the sphere-seated eyes stranded in a side lobe.
    // Drive volume from how far the body has turned off REST (pointer = full).
    const petaVolDrive =
      petaK > 0.5
        ? bodyFace && bodyDrive > 0.01
          ? 1
          : clamp(
              Math.hypot(volGaze.yaw - REST_GAZE.yaw, volGaze.pitch - REST_GAZE.pitch) / 22,
              0,
              1
            )
        : 1
    const volRadii =
      starK > 0
        ? starVolumeRadii(sil.radii, volGaze, starK)
        : petaK > 0
          ? petaVolumeRadii(sil.radii, volGaze, petaK * petaVolDrive)
          : roundK > 0
            ? roundVolumeRadii(sil.radii, volGaze, roundK)
            : sil.radii
    const bodyPts = toPoints({ ...sil, radii: volRadii }, R, this.pts)
    const bodyPath = closedPath(bodyPts)
    const bodyPathBack: string | null = null
    const bodyPathFront: string | null = null
    const volSheenAmt = Math.max(starK, roundK, petaK)
    // Peta idle must stay face-on: a REST-planted sheen skews the highlight and
    // reads as a permanent rightward tilt even with centred upright eyes.
    const sheenDrive =
      petaK > 0.5
        ? bodyDrive > 0.01
          ? Math.max(bodyDrive, 0.45)
          : bodyFace
            ? 1
            : petaVolDrive * 0.35
        : bodyDrive > 0.01
          ? Math.max(bodyDrive, 0.55)
          : bodyFace
            ? 1
            : 0.55
    const volumeSheen =
      volSheenAmt > 0 && sheenDrive > 0.02
        ? starVolumeSheen(volGaze, R, volSheenAmt * sheenDrive)
        : null
    const showUniverse =
      def.id === 'thinking' || def.id === 'glyph' || def.id === 'icons'
        ? false
        : def.id === 'logo' || def.id === 'logoOuter' || def.id === 'sleep'
          ? !pose.notif
          : def.id === 'comet'
            ? Math.max(...pose.sil.radii) >= 0.72
            : isSparkleSilhouette(pose.sil) && !pose.notif

    // --- eyes -------------------------------------------------------------
    // Eyes live on a unit sphere; as soon as the silhouette is no longer a
    // circle, we bring them back to the real radius in their direction, otherwise
    // they overflow and the mask clips them.
    const eyes: RenderedEye[] = []
    const sparkleSil = !def.baseBody && isSparkleSilhouette(pose.sil)
    const sparkleRadii = sparkleSil ? pose.sil.radii : null
    const sparklePeak = sparkleRadii ? Math.max(...sparkleRadii) : 1
    // Collapsed sparkle (comet/sleep planet) cannot host rest-sized eyes — the mask
    // clips them into a notch on a lobe.
    if (pose.eyeAlpha > 0.01 && sparklePeak >= 0.72) {
      // Jovi / Peta: seat on yaw/pitch for *place*. Capsule spin blends face-on →
      // seated: looking straight stays a soft shared \\ (not the video's hard
      // tilt); free-look recovers sphere foreshorten at the rim. Pitch+split on
      // roll:0 alone opens a V; shared slant on that V only deepened the hard tilt.
      // Peta remaps video REST → face-on under the top petal (REST yaw parked
      // the pair in the right lobe on the five-fold glyph).
      const volumeFace = roundK > 0.5 || petaK > 0.5
      // Live pointer / body drive keeps Peta's full look throw; parked catalogue
      // moods compress the REST→camera gap so surpris doesn't inherit a left petal.
      const petaLive =
        look.mix > 0.12 ||
        (typeof look.volume === 'number' && look.volume > 0.08) ||
        bodyDrive > 0.08
      const seatGaze = petaK > 0.5 ? petaSeatGaze(gaze, { live: petaLive }) : gaze
      const seatRest = petaK > 0.5 ? PETA_REST_GAZE : REST_GAZE
      const poses = eyePoses(
        volumeFace ? { yaw: seatGaze.yaw, pitch: seatGaze.pitch, roll: 0 } : gaze,
        R,
        pose.split
      )
      const fromRest = Math.hypot(seatGaze.yaw - seatRest.yaw, seatGaze.pitch - seatRest.pitch)
      const wrapK = volumeFace ? clamp(fromRest / 32, 0, 1) : 0
      // Jovi: sphere orient recovers rim foreshorten. Peta: face-on bases stay
      // parallel (sphere spin opened a V and parked one eye on a petal); a shared
      // tip + width squash tracks the look without the crooked pair.
      const orient = volumeFace
        ? petaK > 0.5
          ? eyePoses({ yaw: 0, pitch: 0, roll: 0 }, R, pose.split)
          : eyePoses(
              { yaw: seatGaze.yaw * wrapK, pitch: seatGaze.pitch * wrapK, roll: 0 },
              R,
              pose.split
            )
        : poses
      const volumeSlantDeg = volumeFace
        ? petaK > 0.5
          ? clamp(seatGaze.yaw * 0.12, -7, 7) * wrapK
          : (-6 + (gaze.roll - REST_GAZE.roll) * 0.2) * (1 - wrapK)
        : 0
      const petaSquash =
        petaK > 0.5 ? lerp(1, 0.78, wrapK * clamp(Math.abs(seatGaze.yaw) / 36, 0, 1)) : 1
      const drafts: Array<{
        d: string
        ax: number
        ay: number
        cx2: number
        cy2: number
        k: number
        ex: number
        ey: number
        alpha: number
      }> = []
      for (let i = 0; i < 2; i++) {
        const e = poses[i]!
        if (e.depth <= 0.02) continue
        const o = orient[i]!
        const cfg = pose.eyes[i]!
        const eyeStyle = def.baseBody
          ? lerpEyeStyle(
              eyeStyleForState(def, this.shapePrev, cfg),
              eyeStyleForState(def, this.shape, cfg),
              shapeMorph
            )
          : sparkleSil
            ? (concavePocketStyle(sparkleRadii) ?? UNIT_EYE)
            : UNIT_EYE
        const liftOn = (e: BotExpression | null, radii: number[] | null) =>
          e ? expressionPocketLift(e, radii) : 0
        const shapeRadii = def.baseBody ? this.shapeAtTime(now) : null
        const exprLift =
          this.exprPrev && this.expr
            ? lerp(liftOn(this.exprPrev, shapeRadii), liftOn(this.expr, shapeRadii), exprK)
            : liftOn(this.expr ?? this.exprPrev, shapeRadii)
        const pocketLift = def.baseFace && (this.expr || this.exprPrev) ? exprLift : 0
        // Seat / pocket rail on the flat customiser profile (eyefit table). Body
        // volume foreshortens the near tip; a later common radial scale keeps the
        // pair in the drawn pocket without melting near-higher-on-look-right.
        const flatRadii = def.baseBody
          ? this.shapeAtTime(now)
          : sparkleSil
            ? sparkleRadii
            : null
        const pocketOutline = flatRadii ?? pocketRadii
        // Sparkle: no pocket-spread — widening reads as a tall hat-man face in
        // the crown join. Other concave shapes still widen on look-down.
        const pocketSpread =
          starK > 0.5
            ? 1
            : pocket && (pocket.nx !== 0 || pocket.ny !== 0)
              ? pocketSpreadScale(pocket.ny, pocketOutline)
              : 1
        const px = e.x * eyeStyle.inset
        const py = e.y * eyeStyle.inset
        const fit = eyeSurfaceFit(def, flatRadii, px, py, pose.sil.rot)
        const fitX = fit.fitX
        const fitY = fit.fitY
        // Per-eye tilt: compose the tangent frame with a rotation in the eye
        // plane (Basis x Rot). This enables mirrored tilts between the two eyes.
        const phi = (((cfg.tilt ?? 0) * eyeStyle.tilt) * Math.PI) / 180
        const cp = Math.cos(phi)
        const sp = Math.sin(phi)
        let ax = o.a * cp + o.c * sp
        let ay = o.b * cp + o.d * sp
        let cx2 = -o.a * sp + o.c * cp
        let cy2 = -o.b * sp + o.d * cp
        if (volumeSlantDeg !== 0) {
          const r = (volumeSlantDeg * Math.PI) / 180
          const c = Math.cos(r)
          const s = Math.sin(r)
          const nax = ax * c + cx2 * s
          const nay = ay * c + cy2 * s
          const ncx = -ax * s + cx2 * c
          const ncy = -ay * s + cy2 * c
          ax = nax
          ay = nay
          cx2 = ncx
          cy2 = ncy
        }
        if (petaSquash !== 1) {
          ax *= petaSquash
          ay *= petaSquash
        }
        // Blink applies AFTER all of that: it is a vertical squash on screen,
        // not along the capsule axis.
        const form = cfg.form ?? 'capsule'
        const rawLid = clamp(Math.min(lids[i]!, cfg.open ?? 1) * lidAccent)
        const pathOpen = form === 'smile' ? blinkScale(rawLid) : 1
        const k = form === 'smile' ? 1 : blinkScale(rawLid)
        const sw = eyeStyle.w
        const sh = eyeStyle.h
        let eyeSlideX = 0
        let eyeSlideY = 0
        if (pocket && (pocket.nx !== 0 || pocket.ny !== 0)) {
          if (starK > 0.5) {
            // Sparkle: no lateral rail (arms steal the crown join). Near-eye
            // height only — look-side high, matching REST's `\\` / left flip.
            eyeSlideX = 0
            eyeSlideY =
              starNearHigherLift(i as 0 | 1, pocket.nx, pocket.ny, pocketOutline) * R
          } else {
            const slide =
              look.mix > 0.2
                ? pocketRailDelta(i as 0 | 1, pocket.nx, pocket.ny, pocketOutline)
                : pocketEyeSlide(i as 0 | 1, pocket.nx, pocket.ny, pocketOutline)
            eyeSlideX = slide.dx * R
            eyeSlideY = slide.dy * R
          }
        } else if (tan && (tan.x !== 0 || tan.y !== 0)) {
          eyeSlideX = (tan.x * e.a + tan.y * e.c) * R
          eyeSlideY = (tan.x * e.b + tan.y * e.d) * R
        }
        // Shape styling scales the capsule path only — not the tangent matrix too,
        // or w/h multipliers compound and neutral eyes turn into paper-thin zeros.
        const seatLift =
          volumeFace ? eyeStyle.lift * volumeBodyLiftScale(seatGaze.pitch) : eyeStyle.lift
        drafts.push({
          d: eyePath(cfg.w * sw * R, cfg.h * sh * R, form, pathOpen),
          ax,
          ay,
          cx2,
          cy2,
          k,
          ex: px * fitX * eyeStyle.spread * pocketSpread + decalage.x * R + eyeSlideX,
          ey: py * fitY + (decalage.y + seatLift + pocketLift) * R + eyeSlideY,
          alpha: pose.eyeAlpha * clamp(e.depth / 0.12)
        })
      }
      // Sparkle: light crown pin on customiser bodies only. Animated loaders /
      // agents keep sphere+volume — nesting fights their measured seats and
      // reads as flat sliding without perspective. Jovi / Peta stay on the
      // circle sphere path (mid-face seat via volumeBodyEyeSeat) — not a tip orbit.
      const pocketLive =
        !!pocket && (pocket.nx * pocket.nx + pocket.ny * pocket.ny > 1e-4)
      const agentLoopEyes = def.id.startsWith('agent')
      const nestAmt =
        starK > 0.5 && def.baseBody
          ? agentLoopEyes
            ? 0.12
            : pocketLive || followAmt > 0.12
              ? lerp(0.72, 0.96, Math.max(followAmt, 0.15))
              : 0
          : 0
      const nestGaze = { yaw: look.yaw, pitch: look.pitch, roll: 0 }
      const nestRadii = def.baseBody ? (this.shapeAtTime(now) ?? volRadii) : volRadii
      const draftXY = drafts.map((d) => ({ x: d.ex, y: d.ey }))
      let nested: { x: number; y: number }[]
      if (nestAmt > 0.05 && starK > 0.5 && pocket) {
        // Flip-symmetric orbit: direction moves the pair smoothly; left extrema
        // are the horizontal flip of the matching right depth (tip included).
        nested = eyePairStarOrbitSeats(draftXY, pocket, nestRadii, R, nestAmt)
      } else if (nestAmt > 0.05) {
        const nest =
          starK > 0.5
            ? eyePairStarHeadOffset(draftXY, nestGaze, nestRadii, R, pose.sil.rot, nestAmt)
            : eyePairNestOffset(draftXY, nestGaze, nestRadii, R, pose.sil.rot, nestAmt)
        nested = draftXY.map((d) => ({ x: d.x + nest.dx, y: d.y + nest.dy }))
      } else {
        nested = draftXY
      }
      // Keep inter-eye spacing near rest — orbit pocket, not a stretched hat face.
      // Attentive→Scared: size-aware floor (Excited discs need >0.42·R), plus pull
      // the pair mid out of the tip so smiles don't ride the lobe as one hole.
      if (starK > 0.5 && nested.length >= 2) {
        const eL = nested[0]!
        const eR = nested[1]!
        const mx = (eL.x + eR.x) / 2
        const my = (eL.y + eR.y) / 2
        const sep = Math.hypot(eR.x - eL.x, eR.y - eL.y)
        const gapMood = starFollowGapMood(expr?.id)
        const pathHalf = (d: string | undefined) => {
          const m = /A([\d.]+) /.exec(d ?? '')
          return m ? Number(m[1]) : R * 0.12
        }
        const need =
          drafts.length >= 2
            ? pathHalf(drafts[0]!.d) + pathHalf(drafts[1]!.d) + R * 0.08
            : R * 0.42
        const minSep = gapMood ? Math.max(R * 0.42, need) : 0
        const maxSep = gapMood ? Math.max(minSep + R * 0.02, R * 0.5) : R * 0.32
        let s = 1
        if (sep > 1e-6 && sep > maxSep) s = maxSep / sep
        else if (sep > 1e-6 && minSep > 0 && sep < minSep) s = minSep / sep
        let next = nested
        if (s !== 1) {
          next = [
            { x: mx + (eL.x - mx) * s, y: my + (eL.y - my) * s },
            { x: mx + (eR.x - mx) * s, y: my + (eR.y - my) * s }
          ]
        }
        if (gapMood && next.length >= 2) {
          const nL = next[0]!
          const nR = next[1]!
          const cx = (nL.x + nR.x) / 2
          const cy = (nL.y + nR.y) / 2
          const sx = cx + offX * R
          const sy = cy + offY * R
          let ncx = cx
          let ncy = cy
          // Horizontal tip only — radial pull toward the origin dropped midY on
          // left/right looks (waist instead of face pocket).
          const xCap = R * 0.36
          if (Math.abs(sx) > xCap) {
            ncx = Math.sign(sx) * xCap - offX * R
          }
          // Hold face height when looking level/up (pocket ny ≤ 0).
          const pocketNy = pocket?.ny ?? 0
          if (pocketNy <= 0.12) {
            const yHigh = -R * 0.36
            if (sy > yHigh) ncy = yHigh - offY * R
          }
          if (ncx !== cx || ncy !== cy) {
            next = [
              { x: ncx + (nL.x - cx), y: ncy + (nL.y - cy) },
              { x: ncx + (nR.x - cx), y: ncy + (nR.y - cy) }
            ]
          }
        }
        nested = next
      }
      for (let i = 0; i < drafts.length; i++) {
        const d = drafts[i]!
        const p = nested[i]!
        // Round / Peta bodies lean for volume. Eyes follow part of that lean so
        // they sit on the surface at the rim — full lean pasted them flat past
        // the limb; zero lean left them sunk inset while the body turned away.
        const eyeOffX = (volumeFace ? offX * 0.42 : offX) * R
        const eyeOffY = (volumeFace ? offY * 0.42 : offY) * R
        eyes.push({
          d: d.d,
          matrix: `matrix(${r2(d.ax)},${r2(d.ay * d.k)},${r2(d.cx2)},${r2(d.cy2 * d.k)},${r2(p.x + eyeOffX)},${r2(p.y + eyeOffY)})`,
          alpha: d.alpha
        })
      }
    }

    // --- decor ------------------------------------------------------------
    const dots = pose.dots
      .filter((p) => p.opacity > 0.01 && (p.r > 0.0005 || p.d))
      .map((p) => ({ ...p, x: (p.x + offX) * R, y: (p.y + offY) * R, r: p.r * R }))

    // the notification dot sits on the outline: it follows the shape too.
    // Keep the measured −42° (upper-right) and hug the real edge so the notch
    // bites the body — diamond corner, star waist, circle rim — instead of
    // walking to a lobe or floating on the orbit ring.
    const nPos = pose.notif
      ? snapToOutline(pose.notif.x, pose.notif.y, volRadii, pose.sil.rot)
      : null
    const nx = nPos ? (nPos.x + offX) * R : 0
    const ny = nPos ? (nPos.y + offY) * R : 0
    const notif = pose.notif ? { x: nx, y: ny, r: pose.notif.r * R } : null
    const notch = pose.notif ? { x: nx, y: ny, r: pose.notif.notch * R } : null

    return {
      bodyPath,
      bodyPathBack,
      bodyPathFront,
      bodyAlpha: pose.bodyAlpha,
      eyes,
      dots,
      dotsBehind: pose.dotsBehind,
      // States declare arcs in ball-radius units; only the engine knows the
      // viewBox scale, so it is the one that rasterises.
      arcs: pose.arcs
        .filter((a) => a.opacity > 0.01)
        .map((a) => arcRender(a.seed, a.t, R, a.id, a.opacity)),
      notif,
      notch,
      gaze,
      bodyGaze: volGaze,
      bodyOffset: { x: r2(offX * R), y: r2(offY * R) },
      volumeSheen,
      // Comet's collapsed sparkle is still a sparkle, but the orbit ring must wait
      // for the body — otherwise the trail plays inside a second, leftover ring.
      // Logo / sleep planets always keep the oval + satellite — their silhouette
      // softens during the pulse and must not drop `isSparkleSilhouette`.
      // Thinking / glyph / icons are faceless bodies — not the Losi orbit oval.
      showUniverse
    }
  }
}
